// Sabit Rapor — Satış Temsilcisi Listesi (Raporlar → Sabit Raporlar → Tanımlamalar).
//
// Temsilci (Representative) tanım kartları. Mantıksal silinen (IsDeleted) temsilciler
// listelenmez — backend süzer.
//
// İki ekran (Sabit Rapor deseni):
//   1) RepresentativeListCriteriaPage — kriter formu (hepsi opsiyonel): Distribütör, Tip, Grup,
//      Çalışma Tipi, Durum, serbest arama. Canlı "N temsilci kritere uyuyor" önizlemesi.
//   2) RepresentativeListListPage — /api/detail-reports/representative-list'ten temsilci kartları
//      + atandığı rut sayısı + #802/#803 mesafe parametreleri. Grid/Grafik/Pivot ReportGrid'den.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/representative-list';
const REPORT_CODE = '5060';
const REPORT_TITLE = 'Satış Temsilcisi Listesi';

// ── Yardımcılar ──
function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o); const name = optName(o); if (code && name) return `${code} - ${name}`; return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
const nfC = new Intl.NumberFormat('tr-TR');
const fmtCount = (v) => nfC.format(Number(v) || 0);
const fmtNum = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 6 }).format(Number(v) || 0));
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

function useLookups() {
  const [lk, setLk] = useState({ vendors: [], repTypes: [], repGroups: [] });
  useEffect(() => {
    const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/representativetypes/Select').then((repTypes) => setLk((s) => ({ ...s, repTypes })));
    load('/api/representativegroups/Select').then((repGroups) => setLk((s) => ({ ...s, repGroups })));
  }, []);
  return lk;
}

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5, width: '100%', boxSizing: 'border-box' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  countChip: { marginTop: 4, padding: '8px 16px', borderRadius: 999, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 13.5 },
};
function Field({ label, children }) { return <label style={c.field}><span style={c.label}>{label}</span>{children}</label>; }
function Select({ value, onChange, options, placeholder = 'Tümü' }) {
  return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>{o.label !== undefined ? o.label : optLabel(o)}</option>
      ))}
    </select>
  );
}

const STATUS_OPTIONS = [{ value: '1', label: 'Aktif' }, { value: '0', label: 'Pasif' }, { value: '2', label: 'Beklemede' }];
// Çalışma Tipi şemada serbest metin (enum değil); sahada kullanılan değerler.
const WORKING_TYPE_OPTIONS = ['Plasiyer', 'Sıcak Satış', 'Soğuk Satış', 'Mix', 'Tüccar']
  .map((t) => ({ value: t, label: t }));
// Çalışma tipi serbest metin alanıdır (şemada enum değil) — seçenekler mevcut veriden türetilir.

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function RepresentativeListCriteriaPage() {
  usePageMeta(REPORT_TITLE, 'Sabit Raporlar › Tanımlamalar');
  const lk = useLookups();
  const [vendorId, setVendorId] = useState([]);
  const [representativeTypeId, setRepresentativeTypeId] = useState([]);
  const [groupId, setGroupId] = useState([]);
  const [workingType, setWorkingType] = useState([]);
  const [status, setStatus] = useState([]);
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null);

  const query = useCallback(() => {
    const p = new URLSearchParams();
    if (vendorId.length) p.set('vendorId', vendorId.join(','));
    if (representativeTypeId.length) p.set('representativeTypeId', representativeTypeId.join(','));
    if (groupId.length) p.set('groupId', groupId.join(','));
    if (workingType.length) p.set('workingType', workingType.join(','));
    if (status.length) p.set('status', status.join(','));
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [vendorId, representativeTypeId, groupId, workingType, status, q]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const p = query();
      p.set('previewOnly', 'true');
      fetch(`${ENDPOINT}?${p.toString()}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (!cancelled) setPreview(json?.data?.totals ?? json?.totals ?? null); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const clear = () => { setVendorId([]); setRepresentativeTypeId([]); setGroupId([]); setWorkingType([]); setStatus([]); setQ(''); };

  const showReport = () => {
    const p = query();
    p.set('embed', 'true');
    const w = Math.min(1720, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/representative-list-list?${p.toString()}`, 'temsilci5060',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };

  return (
    <div style={c.page}>
      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          <Field label="Distribütör"><MultiSelect value={vendorId} onChange={setVendorId} options={asChoices(lk.vendors)} /></Field>
          <Field label="Temsilci Tipi"><MultiSelect value={representativeTypeId} onChange={setRepresentativeTypeId} options={asChoices(lk.repTypes)} /></Field>
          <Field label="Temsilci Grubu"><MultiSelect value={groupId} onChange={setGroupId} options={asChoices(lk.repGroups)} /></Field>
          <Field label="Çalışma Tipi"><MultiSelect value={workingType} onChange={setWorkingType} options={WORKING_TYPE_OPTIONS} /></Field>
        </div>

        <div style={c.sectionSep}><span>DİĞER</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Durum"><MultiSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} /></Field>
          <Field label="Kod / Ad / Plaka Ara">
            <input style={c.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Temsilci kodu, adı, ERP kodu veya plakası" />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="7" r="4" /><path d="M4 21v-2a5 5 0 0 1 5-5h2" /><path d="M15 14h6v6h-6z" /><path d="M17 17h2" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 620 }}>
            Kriterlerin tamamı opsiyoneldir; boş bırakılırsa yetkin dahilindeki tüm temsilciler listelenir.
            Rapor temsilcinin <strong>tip/grup</strong> bilgisini, bağlı <strong>depolarını</strong>,
            atandığı <strong>rut sayısını</strong> ve <strong>mesafe kontrolü</strong> parametrelerini gösterir.
          </span>
          {preview && <div style={c.countChip}>{fmtCount(preview.temsilciSayisi)} temsilci kritere uyuyor</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
const codeChipStyle = { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 };
function DurumChip({ v }) {
  if (!v || v === '—') return '—';
  const tone = v === 'Aktif' ? { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a' }
    : v === 'Pasif' ? { bg: 'var(--bg-input, #f1f5f9)', fg: '#94a3b8' }
      : { bg: 'rgba(217,119,6,0.10)', fg: '#d97706' };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 12 }}>{v}</span>;
}

const COLUMNS = [
  { key: 'temsilciKodu', label: 'Temsilci Kodu', sticky: true, cell: (row) => (row.temsilciKodu ? <span style={codeChipStyle}>{row.temsilciKodu}</span> : '—') },
  { key: 'temsilciAdi', label: 'Temsilci Adı', bold: true },
  { key: 'erpKodu', label: 'ERP Kodu', fmt: dash },
  { key: 'distributor', label: 'Distribütör', fmt: dash },
  { key: 'tip', label: 'Tip', fmt: dash },
  { key: 'grup', label: 'Grup', fmt: dash },
  { key: 'satisMuduru', label: 'Satış Müdürü', fmt: dash },
  { key: 'calismaTipi', label: 'Çalışma Tipi', fmt: dash },
  { key: 'rutSayisi', label: 'Rut', num: true, fmt: fmtCount },
  { key: 'siparisDeposu', label: 'Sipariş Deposu', fmt: dash },
  { key: 'satisDeposu', label: 'Satış Deposu', fmt: dash },
  { key: 'plaka', label: 'Plaka', fmt: dash },
  { key: 'referans', label: 'Referans', fmt: dash },
  { key: 'gunlukZiyaret', label: 'Günlük Ziyaret', fmt: dash },
  { key: 'mesafeKontrol', label: 'Mesafe Kontrolü', fmt: dash },
  { key: 'mesafeMetre', label: 'Mesafe (m)', num: true, agg: 'none', fmt: fmtNum },
  { key: 'mesafeEngeller', label: 'Mesafe Engeller', fmt: dash },
  { key: 'durum', label: 'Durum', cell: (row) => <DurumChip v={row.durum} /> },
];

const l = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
};
const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function printAsPdf({ rows, subtitle }) {
  const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row) => `<tr>${COLUMNS.map((col) => {
    let v = row[col.key];
    if (col.fmt) v = col.fmt(v);
    return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`;
  }).join('')}</tr>`).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title><style>*{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:14px}h1{font-size:14px;color:#1e3a8a;margin:0 0 4px}.crit{font-size:9.5px;color:#475569;margin-bottom:8px}table{width:100%;border-collapse:collapse}th{background:#eef2f7;color:#475569;font-size:8px;text-transform:uppercase;padding:4px 5px;border-bottom:1.5px solid #cbd5e1}td{font-size:9px;padding:3px 5px;border-bottom:1px solid #eef2f7;white-space:nowrap}@page{size:A3 landscape;margin:10mm}</style></head><body><h1>${REPORT_TITLE}</h1><div class="crit">${hesc(subtitle)}</div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); };
}

export function RepresentativeListListPage() {
  usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lk = useLookups();

  const params = useMemo(() => {
    const o = {};
    for (const k of ['vendorId', 'representativeTypeId', 'groupId', 'workingType', 'status', 'q']) { const v = sp.get(k); if (v) o[k] = v; }
    return o;
  }, [sp]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null);
    } catch (e) {
      setError(e.message || 'Hata'); setRows([]);
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportRows = visibleRows ?? rows;
  const nameById = (arr, id) => { const o = arr.find((x) => String(optId(x)) === String(id)); return o ? optName(o) : id; };
  const vendorName = params.vendorId ? nameById(lk.vendors, params.vendorId) : 'Tümü';
  const typeLabel = params.representativeTypeId ? nameById(lk.repTypes, params.representativeTypeId) : 'Tümü';
  const groupLabel = params.groupId ? nameById(lk.repGroups, params.groupId) : 'Tümü';
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === params.status)?.label ?? 'Tümü';
  const summaryText = `Distribütör: ${vendorName} · Tip: ${typeLabel} · Grup: ${groupLabel} · Çalışma: ${params.workingType || 'Tümü'} · Durum: ${statusLabel}${params.q ? ` · Arama: ${params.q}` : ''}`;

  const onExcel = () => {
    const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => row[col.key] }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_satis_temsilcisi_listesi`);
  };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="7" r="4" /><path d="M4 21v-2a5 5 0 0 1 5-5h2" /><path d="M15 14h6v6h-6z" /><path d="M17 17h2" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>
              {loading ? 'Yükleniyor…' : `${fmtCount(totals?.temsilciSayisi ?? rows.length)} temsilci · ${fmtCount(totals?.aktifSayisi ?? 0)} aktif · ${fmtCount(totals?.toplamRut ?? 0)} rut ataması · ${nowLabel}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Distribütör: {vendorName}</span>
        <span style={l.critChip}>Tip: {typeLabel}</span>
        <span style={l.critChip}>Grup: {groupLabel}</span>
        <span style={l.critChip}>Durum: {statusLabel}</span>
        {params.q && <span style={l.critChip}>Arama: {params.q}</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="representative-list"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  );
}
